import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../Landingpage/DashboardLayout";
// import VideoResumeNudge from "./VideoResumeNudge"; // temporarily disabled — confusing candidates on /user-home
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Stack } from '@/components/ui/layout';
import { Heading, Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { FileText, Calendar, Briefcase, ArrowRight } from 'lucide-react';

/**
 * Home component:
 * - Checks for unsubmitted questionnaires on mount.
 * - If any exist, blocks DashboardLayout and shows a modal with only a "Continue" button
 *   which navigates the user to the questionnaire page.
 *
 * Props:
 * - userEmail (optional): if you want to refetch when userEmail changes, pass it in.
 *
 * Notes:
 * - Change the fetch endpoint to match your backend (currently uses '/api/unsubmitted_questionnaires').
 * - The Continue button navigates to the first questionnaire in the list by default.
 */
export default function Home() {
  // Hooks must be declared unconditionally at top level
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [unsubmittedQuestionnaires, setUnsubmittedQuestionnaires] = useState([]);
  const [error, setError] = useState(null);
  const didFetchRefRef = useRef(false);

  // inside your component, keep hooks at top as before

  useEffect(() => {
    let cancelled = false;

    const checkUnsubmittedQuestionnaires = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch("/api/questionnaires/unsubmitted", {
          method: "GET",
          credentials: "same-origin", // keep cookies/session
          headers: { Accept: "application/json" },
        });

        if (!res.ok) {
          console.warn("Failed to fetch unsubmitted questionnaires:", res.status);
          if (!cancelled) {
            setUnsubmittedQuestionnaires([]);
            setLoading(false);
            setError(null);
          }
          return;
        }

        const data = await res.json();
        console.debug("unsubmitted response:", data); // <-- helpful for debugging

        // Accept the shape your Flask route returns:
        // { notifications: [ ... ], unread_count: N }
        let notifications = [];
        if (Array.isArray(data.notifications)) {
          notifications = data.notifications;
        } else if (Array.isArray(data)) {
          // fallback if API is returning raw array
          notifications = data;
        } else if (Array.isArray(data.items)) {
          // other fallback
          notifications = data.items;
        } else {
          notifications = [];
        }

        // Normalize items to what UI expects
        const list = notifications.map((n) => ({
          id: n.id ?? n.questionnaire_id ?? null,
          questionnaire_id: n.questionnaire_id ?? n.id ?? null,
          title: n.job_title ?? n.title ?? `Questionnaire ${n.questionnaire_id ?? n.id ?? ""}`,
          job_title: n.job_title ?? null,
          company_name: n.company_name ?? n.company ?? null,
          due_date: n.due_date ?? null,
          created_at: n.created_at ?? null,
          raw: n, // keep raw for troubleshooting if needed
        }));

        if (!cancelled) {
          setUnsubmittedQuestionnaires(list);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error checking questionnaires:", err);
          setError("Unable to check questionnaires. Please try again.");
          setUnsubmittedQuestionnaires([]);
          setLoading(false);
        }
      }
    };

    checkUnsubmittedQuestionnaires();
    return () => {
      cancelled = true;
    };
  }, []);


  // Navigate to questionnaire id (defaults to first available)
  const goToQuestionnaire = (questionnaireId) => {
    const id = questionnaireId ?? (unsubmittedQuestionnaires[0] && unsubmittedQuestionnaires[0].id);
    if (!id) {
      // no id — go to generic list
      // navigate("/questionnaires");
      return;
    }
    navigate(`/questionnaire?id=${encodeURIComponent(id)}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" aria-live="polite">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" role="status" aria-hidden="true" />
      </div>
    );
  }

  if (unsubmittedQuestionnaires && unsubmittedQuestionnaires.length > 0) {
    return (
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className="max-w-[720px]">
          <DialogHeader>
            <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4 text-2xl">
              📝
            </div>
            <DialogTitle className="text-xl font-bold">You have incomplete questionnaires</DialogTitle>
            <DialogDescription>
              Please complete these questionnaires to unlock full access. You cannot access the dashboard until you
              continue and finish them.
            </DialogDescription>
          </DialogHeader>

          <Stack spacing="sm" className="mt-3 max-h-[60vh] overflow-y-auto">
            {unsubmittedQuestionnaires.map((q) => (
              <Card key={q.id} className="border-b">
                <CardContent className="p-3 flex items-center justify-between">
                  <Stack spacing="xs" className="flex-1">
                    <Heading level="h4" className="text-base font-semibold">
                      {q.title || `Questionnaire #${q.id}`}
                    </Heading>
                    <div className="flex flex-col gap-1">
                      {q.due_date && (
                        <Text variant="muted" className="text-sm flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Due: {new Date(q.due_date).toLocaleDateString()}
                        </Text>
                      )}
                      {q.job_title && (
                        <Text variant="muted" className="text-sm flex items-center gap-1">
                          <Briefcase className="w-3 h-3" />
                          {q.job_title}
                        </Text>
                      )}
                    </div>
                  </Stack>
                  <Button
                    variant="outline"
                    onClick={() => goToQuestionnaire(q.id)}
                    aria-label={`Answer questionnaire ${q.title || q.id}`}
                  >
                    Continue <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {error && (
            <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive text-sm">
              {error}
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // No unsubmitted questionnaires -> render DashboardLayout (plus the
  // one-time video-resume prompt, which polices its own anti-nag rules).
  return (
    <>
      {/* Video resume nudge popup temporarily disabled on /user-home —
          it was confusing candidates. Re-enable by uncommenting below. */}
      {/* <VideoResumeNudge /> */}
      <DashboardLayout />
    </>
  );

  // const [profileCompleted, setProfileCompleted] = useState(false);
  // // const [loading, setLoading] = useState(true);
  // const [showPopup, setShowPopup] = useState(false);
  // const [missingFields, setMissingFields] = useState([]);
  // // const navigate = useNavigate();

  // // useEffect(() => {
  // //   checkProfileCompletion();
  // //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // // }, []);

  // const checkProfileCompletion = async () => {
  //   try {
  //     const response = await fetch('/api/check_profile_completion');
  //     if (response.ok) {
  //       const data = await response.json();
  //       setProfileCompleted(!!data.completed);
  //       setMissingFields(Array.isArray(data.missing_fields) ? data.missing_fields : []);
  //       setShowPopup(!data.completed);
  //     } else {
  //       setShowPopup(true);
  //     }
  //   } catch (error) {
  //     console.error('Error:', error);
  //     setShowPopup(true);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const handleCompleteProfile = () => {
  //   navigate('/crr');
  // };

  // const handleClosePopup = () => {
  //   setShowPopup(false);
  // };

  // if (loading) {
  //   return (
  //     <div className={styles.loadingContainer}>
  //       <div className={styles.spinner} />
  //       <p>Checking your profile…</p>
  //     </div>
  //   );
  // }

  // return (
  //   <>
  //     {showPopup && (
  //       <div className={styles.popupOverlay} role="dialog" aria-modal="true" aria-label="Profile incomplete">
  //         <div className={styles.popup}>
  //           <div className={styles.popupHeader}>
  //             <h2>Profile Incomplete</h2>
  //             <button
  //               className={styles.closeButton}
  //               onClick={handleClosePopup}
  //               aria-label="Close profile incomplete dialog"
  //             >
  //               ×
  //             </button>
  //           </div>

  //           <div className={styles.popupContent}>
  //             <div className={styles.warningIcon}>⚠️</div>

  //             <div>
  //               <h3>Please Complete Your Profile</h3>
  //               <p>To access the dashboard, finish all mandatory fields listed below.</p>

  //               <div className={styles.missingFields}>
  //                 {missingFields && missingFields.length > 0 ? (
  //                   missingFields.map((field, i) => (
  //                     <div key={i} className={styles.missingField}>• {field}</div>
  //                   ))
  //                 ) : (
  //                   <div className={styles.missingField}>• Profile photo and personal info</div>
  //                 )}
  //               </div>

  //               <p className={styles.note}><strong>Note:</strong> Profile photo and all personal information are mandatory.</p>
  //             </div>
  //           </div>

  //           <div className={styles.popupActions}>
  //             <button className={styles.primaryButton} onClick={handleCompleteProfile}>
  //               Complete Profile Now
  //             </button>
  //           </div>
  //         </div>
  //       </div>
  //     )}

  //     {profileCompleted ? (
  //       <DashboardLayout />
  //     ) : (
  //       <div className={styles.homePlaceholder}>
  //         <h2>Welcome 👋</h2>
  //         <p>
  //           Your profile is not complete yet. Finish your profile to unlock the full dashboard experience
  //           (job matches, recommendations and more).
  //         </p>

  //         <div className={styles.placeholderActions}>
  //           <button className={styles.primaryButton} onClick={() => navigate('/crr')}>
  //             Complete Profile
  //           </button>
  //         </div>
  //       </div>
  //     )}
  //   </>
  // );
}